import type { CSSProperties } from 'react';
import type { Block, EmbedBlock, SpacingScale } from '@/types/blocks';

function sizeValue(value: number | null | undefined, unit: 'px' | '%' | undefined) {
  if (!value || value <= 0) return undefined;
  return `${value}${unit ?? 'px'}`;
}

/**
 * Tamanho do iframe do bloco Iframe (embed) — mesmos campos width/height do
 * "Redimensionar bloco" (genéricos a todo bloco), mas aplicados direto no
 * <iframe> em vez do wrapper: sem isso, um iframe sem altura própria some
 * (altura padrão do navegador é só ~150px) e o wrapper (BlockSizeFrame) não
 * empurra a altura para dentro do elemento — ele só define a caixa externa.
 */
export function getEmbedFrameStyle(block: EmbedBlock, fallbackHeightPx = 480): CSSProperties {
  return {
    width: sizeValue(block.width, block.widthUnit) ?? '100%',
    height: sizeValue(block.height, block.heightUnit) ?? `${fallbackHeightPx}px`,
  };
}

/** Mesmos valores físicos do padding do Div (PADDING_CLASS em divStyle.ts: p-0/p-3/p-6/p-10). */
const SPACING_PX: Record<SpacingScale, number> = { none: 0, sm: 12, md: 24, lg: 40 };

function spacingValue(scale: SpacingScale | undefined): number | undefined {
  if (!scale || scale === 'none') return undefined;
  return SPACING_PX[scale];
}

function spacingSides(
  scale: SpacingScale | undefined,
  top: number | undefined,
  right: number | undefined,
  bottom: number | undefined,
  left: number | undefined,
): Pick<CSSProperties, 'marginTop' | 'marginRight' | 'marginBottom' | 'marginLeft'> {
  const fallback = spacingValue(scale);
  return {
    marginTop: top ?? fallback,
    marginRight: right ?? fallback,
    marginBottom: bottom ?? fallback,
    marginLeft: left ?? fallback,
  };
}

function paddingSides(
  scale: SpacingScale | undefined,
  top: number | undefined,
  right: number | undefined,
  bottom: number | undefined,
  left: number | undefined,
): Pick<CSSProperties, 'paddingTop' | 'paddingRight' | 'paddingBottom' | 'paddingLeft'> {
  const fallback = spacingValue(scale);
  return {
    paddingTop: top ?? fallback,
    paddingRight: right ?? fallback,
    paddingBottom: bottom ?? fallback,
    paddingLeft: left ?? fallback,
  };
}

export function getBlockSizeStyle(block: Block, fixed = false): CSSProperties {
  const isFixed = block.sticky && fixed;
  return {
    position: block.sticky ? (isFixed ? 'fixed' : 'sticky') : undefined,
    top: block.sticky ? 0 : undefined,
    zIndex: block.sticky ? 40 : undefined,
    left: isFixed ? 0 : undefined,
    right: isFixed ? 0 : undefined,
    width: sizeValue(block.width, block.widthUnit),
    height: sizeValue(block.height, block.heightUnit),
    maxWidth: block.width && block.width > 0 ? '100%' : undefined,
    ...spacingSides(block.margin, block.marginTop, block.marginRight, block.marginBottom, block.marginLeft),
    // Div já aplica seu próprio espaçamento interno via classe Tailwind no
    // elemento (getDivClassName) — aplicar de novo aqui duplicaria o padding.
    ...(block.type === 'div'
      ? {}
      : paddingSides(block.padding, block.paddingTop, block.paddingRight, block.paddingBottom, block.paddingLeft)),
  };
}
